import { useEffect, useState } from "react";
import { lookupTenantBySlug } from "@/lib/api/auth-login";
import { getSessionTenantSlug, useSession } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { oauthLoginPath } from "@/lib/post-login-redirect";
import { T } from "./oauth-consent-account.i18n";

type Props = {
  /** The consent page's query: the signed OAuth request, which a new sign-in resumes. */
  searchParams: URLSearchParams;
};

/**
 * Who the app will act as: the signed-in user and organization. One browser holds
 * one session, so a user with access to several organizations sees here which one
 * the app gets, and can sign out and pick another without losing the request.
 */
export function OAuthConsentAccount({ searchParams }: Props) {
  const t = useT(T);
  const { data: session } = useSession();
  const slug = getSessionTenantSlug(session);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    lookupTenantBySlug(slug)
      .then((info) => !cancelled && setOrgName(info?.name ?? null))
      // The name is cosmetic: fall back to the slug
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!session?.user) return null;

  // /clear signs out and opens this login page, which finishes the same request.
  const switchHref = `/clear?redirect=${encodeURIComponent(oauthLoginPath(searchParams))}`;

  return (
    <div className="mb-6 rounded-lg border px-4 py-3 text-sm">
      <p className="text-muted-foreground">{t.signedInAs}</p>
      <p className="font-medium break-all">{session.user.email}</p>
      {slug && <p className="text-muted-foreground">{orgName ?? slug}</p>}
      <a
        href={switchHref}
        className="mt-2 inline-block text-primary underline-offset-4 hover:underline"
      >
        {t.useAnother}
      </a>
    </div>
  );
}
